import { useCallback, useEffect, useRef, useState } from 'react';

const KEY_PREFIX = 'info_messages_seen_';
const CHANNEL = 'info_messages';
const TAIL = 200;

const seenKey = (userId: string): string => `${KEY_PREFIX}${userId}`;

/* Array.from вместо [...set] и for..of: при target ES5 перебор Set не компилируется
   без downlevelIteration (TS2802), а поднимать флаг ради двух мест не хочется. */
const copySeen = (source: Set<string>): Set<string> => new Set<string>(Array.from(source));

const loadSeen = (userId: string): Set<string> => {
    try {
        const raw = localStorage.getItem(seenKey(userId));
        const ids: unknown = JSON.parse(raw ?? '[]');
        return new Set<string>(Array.isArray(ids) ? (ids as string[]) : []);
    } catch {
        return new Set<string>();
    }
};

const saveSeen = (userId: string, ids: Set<string>): void => {
    try {
        localStorage.setItem(seenKey(userId), JSON.stringify(Array.from(ids).slice(-TAIL)));
    } catch {
        /* приватный режим / переполнение — не критично */
    }
};

interface Seen {
    seen: Set<string>;
    markSeen: (ids: string[]) => void;
}

/** Ключ на пользователя: на общем компьютере аккаунты не должны гасить объявления друг другу. */
export const useSeenNotifications = (userId?: string): Seen => {
    const [seen, setSeen] = useState<Set<string>>(() =>
        userId ? loadSeen(userId) : new Set<string>(),
    );
    const seenRef = useRef(seen);
    const channelRef = useRef<BroadcastChannel | null>(null);

    const apply = useCallback((next: Set<string>) => {
        seenRef.current = next;
        setSeen(next);
    }, []);

    useEffect(() => {
        if (!userId) {
            apply(new Set<string>());
            return undefined;
        }

        apply(loadSeen(userId));

        const channel = 'BroadcastChannel' in window ? new BroadcastChannel(CHANNEL) : null;
        channelRef.current = channel;

        if (channel) {
            /* Чтобы плашка не всплывала в каждой вкладке отдельно. */
            channel.onmessage = (event: MessageEvent<{ ids?: string[] }>) => {
                const ids = event.data?.ids;
                if (!Array.isArray(ids) || !ids.length) return;

                const next = copySeen(seenRef.current);
                ids.forEach((id) => next.add(id));
                apply(next);
            };
        }

        const onStorage = (event: StorageEvent): void => {
            if (event.key === seenKey(userId)) apply(loadSeen(userId));
        };
        window.addEventListener('storage', onStorage);

        return () => {
            channel?.close();
            channelRef.current = null;
            window.removeEventListener('storage', onStorage);
        };
    }, [userId, apply]);

    const markSeen = useCallback(
        (ids: string[]) => {
            if (!userId || !ids.length) return;

            const current = seenRef.current;
            const added = ids.filter((id) => !current.has(id));
            if (!added.length) return;

            const next = copySeen(current);
            added.forEach((id) => next.add(id));

            apply(next);
            saveSeen(userId, next);
            channelRef.current?.postMessage({ ids: added });
        },
        [userId, apply],
    );

    return { seen, markSeen };
};